import { Component } from "@theme/component";
import { getLenis } from "@theme/utilities";

/**
 * Opens a PhotoSwipe lightbox for the items ([data-zoom-item]) inside it, from the trigger button of each item.
 * The lightbox contains every zoomable item (images and videos), in DOM order.
 * When it closes, the layout is scrolled to the last viewed item (carousel, swipe on mobile or grid).
 *
 * @extends Component
 */
export class MediaGalleryZoom extends Component {
  /** @type {Promise<{ openPswp: Function }> | null} */
  #zoomDialogModule = null;

  /**
   * Preloads the zoom dialog module on hover for better UX.
   */
  preloadZoomDialog = () => {
    if (this.#zoomDialogModule) return;
    this.#zoomDialogModule = import("@theme/zoom-dialog");
  };

  /**
   * Opens the zoom dialog at the clicked image.
   *
   * @param {Event} event
   */
  openZoomDialog = async (event) => {
    const trigger = /** @type {HTMLElement | null} */ (/** @type {HTMLElement} */ (event.target).closest("button"));
    const clickedItem = trigger?.closest("[data-zoom-item]");
    if (!trigger || !clickedItem) return;

    trigger.setAttribute("aria-busy", "true");

    try {
      /** @type {HTMLElement[]} */
      const nodes = Array.from(this.querySelectorAll("[data-zoom-item]"));

      const items = nodes.map((node) => {
        const { mediaType, mediaSrc, mediaWidth, mediaHeight, mediaMime, mediaPoster, mediaLabel } = node.dataset;

        if (mediaType === "video") {
          const video = document.createElement("video");
          video.className = "media-gallery-zoom__video";
          video.controls = true;
          video.playsInline = true;
          video.preload = "metadata";
          if (mediaPoster) video.poster = mediaPoster;
          if (mediaLabel) video.setAttribute("aria-label", mediaLabel);

          const source = document.createElement("source");
          source.src = mediaSrc ?? "";
          if (mediaMime) source.type = mediaMime;
          video.append(source);

          const wrapper = document.createElement("div");
          wrapper.className = "media-gallery-zoom__slide";
          wrapper.append(video);

          return { type: "html", html: wrapper.outerHTML, element: node };
        }

        return {
          src: mediaSrc,
          alt: mediaLabel ?? "",
          width: Number(mediaWidth),
          height: Number(mediaHeight),
          element: node,
        };
      });

      const startIndex = Math.max(0, nodes.indexOf(/** @type {HTMLElement} */ (clickedItem)));

      const zoomDialogModule = this.#zoomDialogModule || import("@theme/zoom-dialog");
      this.#zoomDialogModule = zoomDialogModule;

      /** @type {HTMLElement | undefined} */
      let lastNode = clickedItem instanceof HTMLElement ? clickedItem : undefined;

      const { openPswp } = await zoomDialogModule;
      openPswp(items, startIndex, {
        trigger,
        // Thumbnails are cropped (1:1) while the popup uses the original ratio, so a zoom transition would not line up
        showHideAnimationType: "fade",
        // Focus is restored manually (see onDestroy) so the carousel's focusin handler does not jump back to the clicked slide
        returnFocus: false,
        onClose: (pswpIndex) => {
          lastNode = nodes[pswpIndex];
          this.#syncCarousel(lastNode);
        },
        onDestroy: () =>
          lastNode?.querySelector('button[on\\:click*="openZoomDialog"]')?.focus({ preventScroll: true }),
      });
    } catch (error) {
      console.error("Failed to open zoom dialog:", error);
    } finally {
      trigger.removeAttribute("aria-busy");
    }
  };

  /**
   * After the dialog closes, slides the carousel to the slide of the last viewed item.
   * Works with any carousel that extends CarouselComponent (flex-carousel, carousel-slider, ...):
   * the closest ancestor exposing a live `swiperInstance` is used, wherever it sits relative to this element.
   * Without a live Swiper (destroyed carousel, swipe on mobile, grid) the item is scrolled into view instead.
   *
   * @param {HTMLElement | undefined} node
   */
  #syncCarousel(node) {
    if (!node) return;

    /** @type {any} */
    let swiper = null;
    for (let el = node.parentElement; el; el = el.parentElement) {
      const instance = /** @type {any} */ (el).swiperInstance;
      if (instance && !instance.destroyed) {
        swiper = instance;
        break;
      }
    }
    if (!swiper) {
      this.#scrollToItem(node);
      return;
    }

    /** @type {HTMLElement | undefined} */
    const slide = Array.from(swiper.slides ?? []).find((slideEl) => slideEl.contains(node));
    if (!slide) return;

    if (swiper.params?.loop) {
      // Looped carousels duplicate slides, so use the original slide index instead of the DOM position
      const realIndex = Number(slide.dataset.swiperSlideIndex);
      if (!Number.isNaN(realIndex)) swiper.slideToLoop(realIndex, 0, false);
      return;
    }

    swiper.slideTo(swiper.slides.indexOf(slide), 0, false);
  }

  /**
   * Scrolls the item into view for layouts without a live Swiper: horizontally inside its scroll container
   * (swipe on mobile), then vertically on the page (grid). Does nothing when the item is already visible.
   *
   * @param {HTMLElement} node
   */
  #scrollToItem(node) {
    this.#scrollHorizontally(node);
    this.#scrollVertically(node);
  }

  /**
   * Only the closest container that really scrolls horizontally is moved (never overflow: hidden containers).
   *
   * @param {HTMLElement} node
   */
  #scrollHorizontally(node) {
    /** @type {HTMLElement | null} */
    let scroller = null;
    for (let el = node.parentElement; el && el !== document.documentElement; el = el.parentElement) {
      const { overflowX } = getComputedStyle(el);
      if ((overflowX === "auto" || overflowX === "scroll") && el.scrollWidth > el.clientWidth + 1) {
        scroller = el;
        break;
      }
    }
    if (!scroller) return;

    const style = getComputedStyle(scroller);
    const scrollerRect = scroller.getBoundingClientRect();
    const nodeRect = node.getBoundingClientRect();
    if (nodeRect.left >= scrollerRect.left - 1 && nodeRect.right <= scrollerRect.right + 1) return;

    // Align the item with the snap position (scroll-padding-inline-start), RTL aware
    const paddingStart = parseFloat(style.scrollPaddingInlineStart) || 0;
    const delta =
      style.direction === "rtl"
        ? nodeRect.right - (scrollerRect.right - paddingStart)
        : nodeRect.left - (scrollerRect.left + paddingStart);

    // Inline scroll-behavior overrides the smooth one set in CSS so the jump is instant
    const previous = scroller.style.scrollBehavior;
    scroller.style.scrollBehavior = "auto";
    scroller.scrollLeft += delta;
    scroller.style.scrollBehavior = previous;
  }

  /**
   * @param {HTMLElement} node
   */
  #scrollVertically(node) {
    const rect = node.getBoundingClientRect();
    const gap = 16;
    const headerHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--header-height")) || 0;

    let delta = 0;
    if (rect.top < headerHeight + gap) {
      delta = rect.top - headerHeight - gap;
    } else if (rect.bottom > window.innerHeight - gap) {
      delta = rect.bottom - window.innerHeight + gap;
    }
    if (!delta) return;

    const lenis = getLenis();
    if (lenis) {
      lenis.scrollTo(window.scrollY + delta, { immediate: true });
      return;
    }

    const root = document.documentElement;
    const previous = root.style.scrollBehavior;
    root.style.scrollBehavior = "auto";
    window.scrollBy(0, delta);
    root.style.scrollBehavior = previous;
  }
}

if (!customElements.get("media-gallery-zoom")) {
  customElements.define("media-gallery-zoom", MediaGalleryZoom);
}
